import React, {useEffect} from 'react';
import {Dimensions, ListRenderItem, View} from 'react-native';
import {FlatList} from 'react-native';
import Modal from 'react-native-modal';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Icon} from '@rneui/themed';
import {Formik} from 'formik';
import {observer} from 'mobx-react';

import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {CartStore} from '../../../stores/cartStore';
import {useStores} from '../../../stores/use-stores';
import {Day} from '../../../transport/apiServiceResponse';
import ActivityIndicator from '../../themed/ActivityIndicator';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import ValidationMessage from '../../themed/ValidationMessage';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

import DayTile, {TimeTile, TimeTileSpacer} from './DayTile';
import {SlotSchema} from './validationSchema';

const PICKUP_SUBTITLE = `Pick up times have a 30 minute window`;
const DELIVERY_SUBTITLE = `Delivery times have a 1 hour window`;

function SlotModal({
  children,
  methodTouched,
}: {
  /**
   * if delivery method switch is touched and selected slot empty then show modal
   */
  methodTouched: boolean;
  children: ({handleOpen}: any) => React.ReactNode;
}) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const {cartStore} = useStores();

  const onClose = () => setIsVisible(false);
  const handleOpen = () => setIsVisible(true);

  const selectDayText = cartStore.isDelivery
    ? 'Select a day for delivery'
    : 'Select a day for pick up';
  const subtitle = cartStore.isDelivery ? DELIVERY_SUBTITLE : PICKUP_SUBTITLE;

  // when delivery method is changed and no delivery slot show the slot modal
  useEffect(() => {
    if (!cartStore.selectedSlot && methodTouched) {
      setIsVisible(true);
    }
  }, [cartStore, cartStore.selectSlot, methodTouched]);

  const insets = useSafeAreaInsets();

  return (
    <>
      {children({handleOpen})}
      <Modal
        isVisible={isVisible}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}
        style={{padding: 0, margin: 0}}>
        <View
          style={{
            paddingTop: insets.top,
            paddingBottom: insets.bottom,
            maxHeight: Dimensions.get('window').height,
            maxWidth: Dimensions.get('window').width,
            backgroundColor: 'white',
            flexGrow: 1,
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 20,
            }}>
            <Text style={{marginTop: 24, marginBottom: 18}} variant="heading1">
              {' ' /* put title here if needed */}
            </Text>
            <PressableWithOpacityFeedback onPress={onClose}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>

          {cartStore.isDelivery && cartStore.noDeliverySlotsAvailable ? (
            <View style={{padding: 20}}>
              <Empty
                noLoading
                message="Delivery is only available within a 5km radius of this store.  Please update your delivery address to see availability, otherwise select Pick Up."
              />
            </View>
          ) : (
            <Formik
              /**
               * let formik rerender with current day default as its lazy loaded
               */
              enableReinitialize
              initialValues={{
                selectedDay: cartStore.selectedDay || cartStore.days?.[0],
                selectedSlot: cartStore.selectedSlot,
              }}
              onSubmit={({selectedDay, selectedSlot}) => {
                setSubmitting(true);
                /**
                 * set day
                 */
                cartStore.selectDay(selectedDay);

                /**
                 * set slot
                 */
                cartStore.selectSlot(selectedSlot, () => {
                  setSubmitting(false);
                  onClose?.();
                });
              }}
              validationSchema={SlotSchema}>
              {({values, errors, touched, handleSubmit, setFieldValue}) => {
                return (
                  <>
                    <View style={{padding: 20, flex: 1}}>
                      <Text
                        center
                        style={{marginBottom: 20}}
                        color="black"
                        variant="heading3">
                        {selectDayText}
                      </Text>
                      <ChooseDay
                        value={values.selectedDay}
                        onChange={day => {
                          setFieldValue('selectedDay', day);
                          setFieldValue('selectedSlot', null);
                        }}
                      />
                      <ValidationMessage>
                        {!!touched.selectedDay && errors.selectedDay}
                      </ValidationMessage>
                      <View style={{flex: 1, flexDirection: 'column'}}>
                        <Text
                          style={{marginTop: 40, marginBottom: 8}}
                          center
                          color="black"
                          variant="heading3">
                          Select a time
                        </Text>
                        <Text
                          style={{marginBottom: 20}}
                          center
                          variant="caption">
                          {subtitle}
                        </Text>
                        <ChooseTime
                          selectedDay={values.selectedDay}
                          value={values.selectedSlot}
                          onChange={value => {
                            setFieldValue('selectedSlot', value);
                          }}
                        />
                        <ValidationMessage>
                          {!!touched.selectedSlot && errors.selectedSlot}
                        </ValidationMessage>
                      </View>
                    </View>
                    <View style={{padding: 20}}>
                      <Button
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        onPress={handleSubmit}>
                        SAVE TIMES
                      </Button>
                    </View>
                  </>
                );
              }}
            </Formik>
          )}
        </View>
      </Modal>
    </>
  );
}

function ItemSeparator() {
  return <View style={{width: 8, height: 8}} />;
}

type ChooseDayProps = {
  value: Day | null;
  onChange: (day: string) => void;
};

const ChooseDay = observer(({value, onChange}: ChooseDayProps) => {
  const {cartStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const isLoading = cartStore.isDelivery
    ? cartStore.loadingDeliverySlots
    : cartStore.loadingPickupSlots;

  const renderDayItem: ListRenderItem<Day> = ({item}) => {
    return <DayTile selected={item === value} onPress={onChange} item={item} />;
  };

  return isLoading ? (
    <Empty />
  ) : (
    <FlatList
      style={{flexGrow: 0}}
      data={cartStore.days || []}
      renderItem={renderDayItem}
      horizontal
      ItemSeparatorComponent={ItemSeparator}
      extraData={value}
      ListEmptyComponent={Empty}
      scrollEnabled
      showsHorizontalScrollIndicator={isWebDesktopScreen}
    />
  );
});

type ChooseTimeProps = {
  value: CartStore['selectedSlot'];
  onChange: (slot: CartStore['selectedSlot']) => void;
  selectedDay: Day | null;
};

const ChooseTime = observer(
  ({value, onChange, selectedDay}: ChooseTimeProps) => {
    const {cartStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    if (!selectedDay) {
      return <Empty message="Please select a day" />;
    }

    const times =
      (!selectedDay
        ? []
        : cartStore.isDelivery
        ? cartStore.deliverySlots[selectedDay]
        : cartStore.pickupSlots[selectedDay]) ?? [];

    const isLoading = cartStore.isDelivery
      ? cartStore.loadingDeliverySlots
      : cartStore.loadingPickupSlots;

    const numColumns = 3;

    const numSpacerTiles =
      Math.ceil(times.length / numColumns) * numColumns - times.length;

    const spacerTiles = Array.from({length: numSpacerTiles}, () => ({
      spacerTile: true,
    }));

    return times?.length === 0 || isLoading ? (
      <Empty message="No times available" />
    ) : (
      <FlatList
        style={{marginHorizontal: -13}}
        scrollEnabled
        showsVerticalScrollIndicator={isWebDesktopScreen}
        data={[...times, ...spacerTiles]}
        numColumns={numColumns}
        renderItem={({item}) =>
          item?.spacerTile ? (
            <TimeTileSpacer />
          ) : (
            <TimeTile
              selected={value?.id === item?.id}
              key={item?.id}
              item={item}
              onPress={onChange}
            />
          )
        }
      />
    );
  },
);

/**
 * Prop default are suitable for choose day flatlist
 * will either show activity loader, message. will only show message if noLoading prop is used
 */
const Empty = observer(
  ({
    message = 'No days available',
    noLoading,
  }: {
    message?: string;
    /**
     * never show the loading indicator. only show the message
     */
    noLoading?: boolean;
  }) => {
    const {cartStore} = useStores();

    const isLoading = cartStore.isDelivery
      ? cartStore.loadingDeliverySlots
      : cartStore.loadingPickupSlots;

    return (
      <View
        style={{
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
        }}>
        {noLoading || !isLoading ? (
          <Text center>{message}</Text>
        ) : isLoading ? (
          <ActivityIndicator />
        ) : null}
      </View>
    );
  },
);

export default observer(SlotModal);
